import React from 'react';
import { BiTrash } from 'react-icons/bi';
import { FaPlus } from 'react-icons/fa6';
import { PiFileText, PiPlant, PiSquaresFour, PiTractor } from 'react-icons/pi';

import { Dropdown, Tooltip } from 'antd';
import type { TreeDataNode } from 'antd';
import Chip from 'components/common/Chip/Chip';
import {
  CROP_KEY_PREFIX,
  FARM_KEY_PREFIX,
  INPUT_KEY_PREFIX,
  ORG_KEY_PREFIX,
  PADDOCK_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  ADD_PLAN_BUTTON_LABEL,
  AREA_UNIT,
  DELETE_PLAN_LABEL,
  OTHER_INPUTS_NODE_LABEL,
  TREE_GROUP_BY_CROP,
  TREE_SEARCH_NO_MATCHES_MESSAGE,
  UNNAMED_INPUT_LABEL,
} from 'constants/uiConstants';
import { IFarm } from 'interfaces/IFarm';
import { IInput } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import {
  buildPaddockNameMap,
  filterInputsByYear,
  formatDocName,
  getInputVarietyName,
  getInputYear,
  groupInputsByCrop,
  inputMatchesTreeSearch,
  paddockMatchesTreeSearch,
  resolvePaddockName,
  sortInputsForCropMode,
  sortPaddocksByName,
  TreeGroupBy,
  TreeYearFilter,
} from 'utils/FarmUtils';
import MergeClassNames from 'utils/MergeClassNames';

import OrgNodeTitle from './OrgNodeTitle';

const ICON_CLASS = 'mr-1.5 flex-shrink-0 text-gray-muted';
const ICON_SIZE = 14;

const formatArea = (
  area: number | null | undefined,
  unit: string | null | undefined
): string | null => {
  if (area == null || area <= 0) return null;
  return `(${area} ${unit || AREA_UNIT})`;
};

export const getInputDisplayName = (input: IInput) =>
  input.crop?.cropName || formatDocName(input.docName) || UNNAMED_INPUT_LABEL;

export const createInputNode = (
  input: IInput,
  onDeleteInput?: (input: IInput) => void,
  // Crop mode labels a plan by its paddock instead of its crop, since the crop
  // is already the parent node. Falls back to the crop name when the plan has
  // no resolvable paddock.
  primaryLabel?: string | null,
  // Whether this is the plan currently open in the workspace. Kept separate
  // from antd's own selection, which follows the last-clicked node and so moves
  // away as soon as the user clicks a farm or paddock.
  isOpen?: boolean
): TreeDataNode => {
  // Must be the same year the filter buckets this plan under, so a node can
  // never appear under one year wearing another year's label. `getYearFromDate`
  // is deliberately not used: it falls back to the current year for an undated
  // plan and reads a local-timezone year off UTC-midnight dates.
  const year = getInputYear(input);
  const variety = getInputVarietyName(input);
  const title = (
    <span className="flex min-w-0 flex-1 items-center">
      <PiFileText className={ICON_CLASS} size={ICON_SIZE} />
      <span className="flex min-w-0 flex-1 flex-col justify-center leading-tight">
        <span className="flex min-w-0 items-center">
          <span className="truncate">
            {primaryLabel || getInputDisplayName(input)}
          </span>
          {year !== null && (
            <span className="ml-1 flex items-center text-nowrap text-xs font-semibold text-gray-muted">
              {`(${year})`}
            </span>
          )}
        </span>
        {variety && (
          <span className="plan-variety truncate text-xs font-medium text-gray-dark">
            {variety}
          </span>
        )}
      </span>
    </span>
  );

  return {
    key: `${INPUT_KEY_PREFIX}${input.id}`,
    // antd renders no key-derived class, so the open-plan marker rides on the
    // node's className — the same mechanism org-root-node and divider-node use.
    className: MergeClassNames('tree-node-plan', isOpen && 'open-plan-node'),
    // Wrapped only when a handler is supplied, so the same builder still works
    // in read-only contexts.
    title: onDeleteInput ? (
      <Dropdown
        trigger={['contextMenu']}
        menu={{
          items: [
            {
              key: 'delete',
              // No `danger` styling — the grid's own row context menu
              // (TaskRowContextMenu) doesn't red-flag delete either, so this
              // stays visually consistent with it rather than singling itself
              // out as more alarming.
              label: <span className="text-gray-dark">{DELETE_PLAN_LABEL}</span>,
              icon: <BiTrash size={ICON_SIZE} className="text-secondary-dark" />,
              onClick: ({ domEvent }) => {
                // Keeps the click off the tree node, which would select it.
                domEvent.stopPropagation();
                onDeleteInput(input);
              },
            },
          ],
        }}
      >
        {title}
      </Dropdown>
    ) : (
      title
    ),
    isLeaf: true,
  };
};

export const createPaddockNode = (
  paddock: IPaddock,
  inputs: IInput[],
  openInputId?: string | null,
  onAddPlan?: (paddock: IPaddock) => void,
  onDeleteInput?: (input: IInput) => void
): TreeDataNode => {
  const children = inputs.map((input) =>
    createInputNode(input, onDeleteInput, undefined, input.id === openInputId)
  );
  // Captured before the ghost row (below) is pushed onto `children`, so it
  // reflects whether the paddock has any *real* plans.
  const hasNoPlans = children.length === 0;

  // A paddock with no plan for the selected year already stays in the tree
  // (see buildFarmTreeData) but would otherwise show no children at all —
  // this ghost row is what actually keeps the "add a plan here" prompt
  // visible, rather than relying solely on the row's hover pill.
  if (hasNoPlans && onAddPlan) {
    children.push({
      key: `${PADDOCK_KEY_PREFIX}${paddock.paddockId}-ghost-plan`,
      selectable: false,
      checkable: false,
      isLeaf: true,
      className: 'ghost-plan-node',
      title: (
        <button
          type="button"
          aria-label={`${ADD_PLAN_BUTTON_LABEL} to ${paddock.paddockName}`}
          title={ADD_PLAN_BUTTON_LABEL}
          className="flex items-center gap-1.5 py-0.5 text-gray-muted"
          onClick={(event) => {
            event.stopPropagation();
            onAddPlan(paddock);
          }}
        >
          <Chip id={`add-plan-${paddock.paddockId}`} label="+ Plan" />
        </button>
      ),
    });
  }

  return {
    key: `${PADDOCK_KEY_PREFIX}${paddock.paddockId}`,
    className: 'tree-node-paddock',
    title: (
      <span className="group relative flex min-w-0 flex-1 items-center">
        <PiSquaresFour className={ICON_CLASS} size={ICON_SIZE} />
        {/* No shrink priority set here — `truncate` already lets this give way
            gracefully; the area suffix (fixed size, hidden outright below a
            width threshold rather than squeezed) is what actually yields the
            space back to the name as the row narrows. */}
        <span className="truncate">{paddock.paddockName}</span>
        {formatArea(paddock.paddockArea, paddock.areaUnit?.unit) && (
          <span className="tree-area-suffix ml-1 flex-shrink-0 text-nowrap text-xs font-semibold text-gray-muted">
            {formatArea(paddock.paddockArea, paddock.areaUnit?.unit)}
          </span>
        )}
        {/* Suppressed when the ghost "no plan yet" row is already showing its
            own "Add plan" pill below — the row-level hover pill would just
            be a redundant second entry point to the same action. */}
        {onAddPlan && !hasNoPlans && (
          <Tooltip title="Add Paddock Plan" placement="right" mouseEnterDelay={0.5}>
          <button
            type="button"
            aria-label={`Add plan to ${paddock.paddockName}`}
            // Absolutely positioned rather than an in-flow flex child: an
            // opacity-0 flex child still reserves its box in the row's
            // layout, which was truncating the paddock name even when the
            // button wasn't visible. Overlaying it instead means the name
            // gets the row's full width at rest, and only visually gives up
            // the trailing sliver it covers once hovered.
            // The padding is a halo around the dark pill, not a hard edge
            // straight onto the paddock name it overlaps — softens where the
            // button cuts across the row's own text. Its colour (.create-
            // plan-halo, farmTreeStyle.css) tracks whether the row is
            // selected rather than being a fixed white: a hard-coded white
            // looked mismatched sitting on top of a selected (green) row.
            // inset-y-0 + flex/items-center centers reliably against this
            // row's full height (including its own padding), rather than a
            // top-50%/-translate trick that centers against this title
            // span's un-padded content box instead.
            className="create-plan-halo absolute inset-y-0 right-0 flex items-center rounded-full p-1 opacity-0 transition-opacity duration-150 focus:opacity-100 focus:outline-none group-hover:opacity-100"
            onClick={(event) => {
              // Keeps the click off the tree node, which would toggle selection.
              event.stopPropagation();
              onAddPlan(paddock);
            }}
          >
            {/* Hand-built rather than <Chip>: shape and label both need to
                react to the row's available width — .create-plan-pill /
                .create-plan-label in farmTreeStyle.css — which Chip's fixed
                label prop can't do. */}
            <span className="create-plan-pill">
              {/* A stroke overlay is what actually thickens the glyph: FaPlus
                  is a filled path with no stroke of its own, so painting one
                  in the same colour widens the arms without lengthening them
                  — a bigger size alone would push the glyph to the circle's
                  edge instead. */}
              <FaPlus size={10} className="[stroke-width:14] [stroke:currentColor]" />
              <span className="create-plan-label">Plan</span>
            </span>
          </button>
          </Tooltip>
        )}
      </span>
    ),
    children,
  };
};

export const createCropNode = (
  cropKey: string,
  cropName: string,
  children: TreeDataNode[]
): TreeDataNode => ({
  // The key comes from groupInputsByCrop rather than being re-derived here, so
  // there is one place a crop key is minted and no way for two nodes to collide.
  key: `${CROP_KEY_PREFIX}${cropKey}`,
  title: (
    <span className="flex min-w-0 flex-1 items-center">
      <PiPlant className={ICON_CLASS} size={ICON_SIZE} />
      <span className="truncate">{cropName}</span>
      <span className="ml-1 flex items-center text-nowrap text-sm text-gray-muted">
        {`(${children.length})`}
      </span>
    </span>
  ),
  children,
});

export const createFarmNode = (
  farm: IFarm,
  children: TreeDataNode[]
): TreeDataNode => ({
  key: `${FARM_KEY_PREFIX}${farm.id}`,
  className: 'tree-node-farm',
  title: (
    <span className="flex min-w-0 flex-1 items-center">
      <PiTractor className={ICON_CLASS} size={ICON_SIZE} />
      <span className="truncate font-semibold">{farm.farmName}</span>
      {formatArea(farm.farmArea, farm.areaUnit?.unit) && (
        <span className="tree-area-suffix ml-1 flex flex-shrink-0 items-center text-nowrap text-xs font-semibold text-gray-muted">
          {formatArea(farm.farmArea, farm.areaUnit?.unit)}
        </span>
      )}
    </span>
  ),
  children,
});

/* ── Org root node ──────────────────────────────────────────── */

export const createOrgNode = (
  orgName: string,
  orgId: string,
  children: TreeDataNode[]
): TreeDataNode => ({
  key: `${ORG_KEY_PREFIX}${orgId}`,
  title: <OrgNodeTitle orgName={orgName} />,
  children,
  className: 'org-root-node',
});

/* ── Tree data builders ─────────────────────────────────────── */

// Shown in place of the tree's children when a search matches nothing, in
// either grouping mode. Deliberately not the farm → paddock ghost skeleton:
// that would read as "these paddocks match" when none do.
const createSearchNoMatchesNode = (): TreeDataNode => ({
  key: 'search-no-matches',
  selectable: false,
  checkable: false,
  isLeaf: true,
  className: 'ghost-plan-node',
  title: (
    <span className="flex items-center py-0.5 text-xs italic text-gray-muted">
      {TREE_SEARCH_NO_MATCHES_MESSAGE}
    </span>
  ),
});

// Crop mode is two levels: crop → plan. Unlike farm mode there is no structural
// skeleton to preserve, so a crop with no plans in the selected year simply
// does not appear — but if that leaves zero crops at all, it falls back to
// the same farm → paddock ghost skeleton farm-mode shows for an empty year
// (see below), just without the crop grouping, rather than an org root with
// nothing under it.
const buildCropTreeData = (
  allInputs: IInput[],
  farms: IFarm[],
  yearFilter: TreeYearFilter,
  openInputId?: string | null,
  onAddPlan?: (paddock: IPaddock) => void,
  onDeleteInput?: (input: IInput) => void,
  searchTerm = ''
): TreeDataNode[] => {
  // Built once and shared by the search, the sort and the labels, rather than
  // rescanning every farm and paddock per group and again per plan.
  const paddockNames = buildPaddockNameMap(farms);

  const yearInputs = filterInputsByYear(allInputs, yearFilter);
  // Crop mode has no paddock nodes, so a paddock-name match is made against
  // the plan's own resolved paddock instead.
  const visibleInputs = searchTerm
    ? yearInputs.filter(
        (input) =>
          inputMatchesTreeSearch(input, searchTerm) ||
          (resolvePaddockName(input, paddockNames) ?? '')
            .toLowerCase()
            .includes(searchTerm)
      )
    : yearInputs;

  const cropGroups = groupInputsByCrop(visibleInputs);

  if (cropGroups.length === 0) {
    if (searchTerm) return [createSearchNoMatchesNode()];

    // No farms to list paddocks from either — fall back to the flat message
    // rather than an org root with nothing under it.
    if (farms.length === 0) {
      return [
        {
          key: 'crop-mode-empty',
          selectable: false,
          checkable: false,
          isLeaf: true,
          className: 'ghost-plan-node',
          title: (
            <span className="flex items-center py-0.5 text-xs italic text-gray-muted">
              No plans for the selected year
            </span>
          ),
        },
      ];
    }

    return farms.map((farm) => {
      const paddockNodes = sortPaddocksByName(farm.paddocks).map((paddock) =>
        createPaddockNode(paddock, [], openInputId, onAddPlan, onDeleteInput)
      );
      return createFarmNode(farm, paddockNodes);
    });
  }

  return cropGroups.map(
    ({ cropKey, cropName, inputs }) => {
      const planNodes = sortInputsForCropMode(inputs, paddockNames).map(
        (input) =>
          createInputNode(
            input,
            onDeleteInput,
            // The crop name is already the parent node's label, so a plan with
            // no resolvable paddock falls back to its document name rather than
            // repeating the crop it sits under. `||` because formatDocName
            // returns an empty string for a name that was nothing but a
            // timestamp, and an empty label would fall through to the crop.
            resolvePaddockName(input, paddockNames) ||
              formatDocName(input.docName) ||
              UNNAMED_INPUT_LABEL,
            input.id === openInputId
          )
      );
      return createCropNode(cropKey, cropName, planNodes);
    }
  );
};

export const buildFarmTreeData = (
  farms: IFarm[],
  paddockInputs: Map<string, IInput[]>,
  farmInputs: Map<string, IInput[]>,
  otherInputs: IInput[],
  yearFilter: TreeYearFilter,
  groupBy: TreeGroupBy,
  allInputs: IInput[],
  openInputId?: string | null,
  onAddPlan?: (paddock: IPaddock) => void,
  onDeleteInput?: (input: IInput) => void,
  // Already normalised (normaliseTreeSearch); empty means no search.
  searchTerm = ''
): TreeDataNode[] => {
  // Crop mode has no structural dependency on farms — it groups plans directly
  // — so the empty-farms guard belongs to the farm-mode path only. Guarding
  // earlier would blank the tree for an org that has plans but no farms.
  if (groupBy === TREE_GROUP_BY_CROP) {
    return buildCropTreeData(
      allInputs,
      farms,
      yearFilter,
      openInputId,
      onAddPlan,
      onDeleteInput,
      searchTerm
    );
  }

  if (farms.length === 0) return [];

  const matchesSearch = (input: IInput) =>
    !searchTerm || inputMatchesTreeSearch(input, searchTerm);

  // The year filter is applied to plan lists only — farms and paddocks always
  // render, so a paddock with no plan for the selected year stays reachable and
  // can still be given one via the "+" button. A search, by contrast, prunes
  // the structure too: a paddock stays when its name matches (with all of its
  // plans) or when any of its plans match on crop or variety, and a farm stays
  // when anything under it does.
  const farmNodes: TreeDataNode[] = [];
  for (const farm of farms) {
    const paddockNodes: TreeDataNode[] = [];
    for (const paddock of sortPaddocksByName(farm.paddocks)) {
      const yearInputs = filterInputsByYear(
        paddockInputs.get(paddock.paddockId) ?? [],
        yearFilter
      );
      const isPaddockMatch =
        !searchTerm || paddockMatchesTreeSearch(paddock, searchTerm);
      const paddockPlans = isPaddockMatch
        ? yearInputs
        : yearInputs.filter(matchesSearch);
      if (!isPaddockMatch && paddockPlans.length === 0) continue;

      paddockNodes.push(
        createPaddockNode(
          paddock,
          paddockPlans,
          openInputId,
          onAddPlan,
          onDeleteInput
        )
      );
    }

    const farmLevelInputs = filterInputsByYear(
      farmInputs.get(farm.id) ?? [],
      yearFilter
    )
      .filter(matchesSearch)
      .map((input) =>
        createInputNode(
          input,
          onDeleteInput,
          undefined,
          input.id === openInputId
        )
      );

    const farmChildren = [...paddockNodes, ...farmLevelInputs];
    if (searchTerm && farmChildren.length === 0) continue;

    farmNodes.push(createFarmNode(farm, farmChildren));
  }

  const visibleOtherInputs = filterInputsByYear(otherInputs, yearFilter).filter(
    matchesSearch
  );

  if (searchTerm && farmNodes.length === 0 && visibleOtherInputs.length === 0) {
    return [createSearchNoMatchesNode()];
  }

  if (visibleOtherInputs.length > 0) {
    farmNodes.push({
      key: 'divider',
      title: <div className="my-2 border-t border-gray-300" />,
      selectable: false,
      checkable: false,
      className: 'divider-node',
    });

    farmNodes.push({
      key: 'other-inputs',
      title: <div>{OTHER_INPUTS_NODE_LABEL}</div>,
      children: visibleOtherInputs.map((input) =>
        createInputNode(
          input,
          onDeleteInput,
          undefined,
          input.id === openInputId
        )
      ),
    });
  }

  return farmNodes;
};
